import { apollo, Clients, useQuery } from "../apollo";
import { GetUsersQuery } from "../graphql/queries/getUsers";
import {
  AWSRegions,
  ExtendedGetUsersResponse,
  ExtendedUserResponse,
  GetUsersFilterInput,
  GetUsersInput,
} from "../generated/ups/graphql";
import { USER_SEARCH_LIMIT } from "../constants/user";
import { DEFAULT_REGION } from "../constants/regions";
import { useEffect, useRef, useState } from "react";
import { exclude } from "../utils/exclude";
import { makeCancelable } from "../utils/makeCancelable";
import { UIUserStatus } from "../types/user";

type UseGetUsersType = GetUsersFilterInput & {
  limit?: number;
  region?: AWSRegions;
  transform?: (users: ExtendedGetUsersResponse["users"]) => unknown;
  key: string;
  status: UIUserStatus[];
};

type GetUsersResponseType = {
  getUsers: ExtendedGetUsersResponse;
};

export const useGetUsers = <TResult = ExtendedUserResponse,>({
  limit = USER_SEARCH_LIMIT,
  region = DEFAULT_REGION,
  roles,
  groups,
  status = [],
  key,
  transform,
}: UseGetUsersType): {
  data: TResult[] | undefined;
  isLoading: boolean;
  fetchUsers: () => Promise<void>;
  next: () => Promise<void>;
  prev: () => void;
  page: number;
  isHasNextPage?: boolean;
} => {
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState<Omit<
    ExtendedGetUsersResponse["lastEvaluatedKey"],
    "__typename"
  > | null>(null);
  const [users, setUsers] = useState<
    ExtendedGetUsersResponse["users"] | undefined
  >(undefined);
  const history = useRef<Array<ExtendedGetUsersResponse["users"]>>([]);
  const cancelableRef = useRef<() => void>();

  const { refetch } = useQuery<GetUsersResponseType, { params: GetUsersInput }>(
    Clients.UPS,
    GetUsersQuery,
    {
      skip: true,
    },
  );

  const getParams = () => {
    return {
      params: {
        region,
        filter: {
          roles: !roles?.length && !groups?.length ? ["*"] : roles || [],
          groups: !roles?.length && !groups?.length ? ["*"] : groups || [],
          status: status.length > 0 ? status : undefined,
        },
        page: {
          limit,
          ...(lastEvaluatedKey ? { lastEvaluatedKey } : {}),
        },
      },
    };
  };

  const fetchUsers = async () => {
    const params = getParams();
    setIsLoading(true);
    const { promise, cancel } = makeCancelable(refetch(params));
    cancelableRef.current = cancel;

    let res: apollo.ApolloQueryResult<GetUsersResponseType> | undefined =
      undefined;
    try {
      res = await promise;
    } catch (error) {
      /* was cancelled */
    }

    if (!res) {
      setIsLoading(false);
      return;
    }

    setUsers(res.data.getUsers.users);
    setLastEvaluatedKey(
      res.data.getUsers.lastEvaluatedKey
        ? exclude(res?.data.getUsers.lastEvaluatedKey ?? {}, [
            "__typename" as never,
          ])
        : null,
    );
    setIsLoading(false);
    history.current = [...(history.current ?? []), res?.data.getUsers.users];
  };

  const next = async () => {
    if (history.current[page + 1]) {
      setUsers(history.current[page + 1]);
      setPage((prev) => prev + 1);
      return;
    }

    if (!lastEvaluatedKey) {
      return;
    }
    await fetchUsers();
    setPage((prev) => prev + 1);
  };

  const reset = () => {
    cancelableRef.current?.();
    setPage(0);
    setLastEvaluatedKey(null);
    setUsers(undefined);
    history.current = [];
  };

  const prev = () => {
    if (page === 0) {
      return;
    }

    setPage((prev) => prev - 1);
    setUsers(history.current[page - 1]);
  };

  useEffect(() => {
    reset();
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const isHasNextPage = Boolean(
    lastEvaluatedKey !== null || history.current[page + 1],
  );

  return {
    data: (transform && users ? transform(users) : users) as TResult[],
    isLoading,
    fetchUsers,
    next,
    prev,
    page,
    isHasNextPage,
  };
};
